import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { Line2d } from '@/editor/math/Line2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { intersectCommonShape } from '@/editor/math/shape/intesection';
import { ESeparateWallType } from '@/editor/data/separateWall/SeparateWall';
import { IShapePoint2d, IShapeSeg2d } from '@/editor/types/shape';
import { thresholdValue } from '@/editor/math/const';

export interface IAuxiliarySegParams {
  start: IPoint2d;
  end: IPoint2d;
  thickness: number;
  type: ESeparateWallType;
  center?: IPoint2d;
  clockwise?: boolean;
  /** 墙体轮廓环；传入时将分隔线裁剪到该轮廓，不传则使用完整的起点到终点偏移线 */
  shape?: IShapePoint2d[];
}

/**
 * 轮廓环(drawShape)拆成边：点上的 center/clockwise 描述【从该点出发】的那条边是弧(见 getWallDrawShape)。
 */
function ringSegs(shape: IShapePoint2d[]): IShapeSeg2d[] {
  const segs: IShapeSeg2d[] = [];
  for (let i = 0; i < shape.length; i++) {
    const a = shape[i];
    const b = shape[(i + 1) % shape.length];
    if (Point2dUtils.distanceTo(a, b) <= thresholdValue) continue;
    segs.push(
      a.center
        ? new ArcSeg2d({
            start: a,
            end: b,
            center: a.center,
            clockwise: !!a.clockwise
          })
        : new LineSeg2d({ x: a.x, y: a.y }, { x: b.x, y: b.y })
    );
  }
  return segs;
}

const segShape = (seg: IShapeSeg2d) =>
  seg instanceof ArcSeg2d ? seg.createCircle() : new Line2d({ points: { p1: seg.start, p2: seg.end } });
/** 交点在该边的实际范围内(含端点，带一点容差) */
const onSeg = (seg: IShapeSeg2d, p: IPoint2d) => seg.pointOnSegment(p);
/** 有符号参数：直线段为 t、弧段为扫角比例，可以 <0 或 >1(即落在段外的延长处) */
const ratioOf = (seg: IShapeSeg2d, p: IPoint2d) =>
  seg instanceof ArcSeg2d ? seg.getExactFootRatio(p) : seg.getFootRatio(p);
const pointAt = (seg: IShapeSeg2d, ratio: number) => seg.getPointFromRatio(ratio);
const trim = (seg: IShapeSeg2d, from: number, to: number): IShapeSeg2d => {
  const start = pointAt(seg, from);
  const end = pointAt(seg, to);
  if (Point2dUtils.distanceTo(start, end) <= thresholdValue) return seg;
  return seg instanceof ArcSeg2d
    ? new ArcSeg2d({ start, end, center: seg.center, clockwise: seg.clockwise })
    : new LineSeg2d(start, end);
};

/**
 * 把一条分隔辅助线裁剪到墙体轮廓环内。
 * 分隔线(偏移弧/偏移线/中线)与轮廓的两条侧边同心/平行、只会与【端盖】相交，所以做法是：
 * 求出分隔线与轮廓各边的所有交点参数，取包含分隔线中点(必在墙体内)的那一段连续区间。
 * 这样端盖被斜接/裁切/折成多点阶梯后，分隔线都会跟着缩到轮廓里，而不是一路画到中线端点之外。
 */
function clipToShape(seg: IShapeSeg2d, shape?: IShapePoint2d[]): IShapeSeg2d {
  if (!shape || shape.length < 3) return seg;

  const ratios: number[] = [];
  for (const edge of ringSegs(shape)) {
    const ips = intersectCommonShape(segShape(seg), segShape(edge));
    for (const p of ips || []) {
      if (!onSeg(edge, p)) continue;
      const r = ratioOf(seg, p);
      if (Number.isFinite(r)) ratios.push(r);
    }
  }
  if (!ratios.length) return seg;

  // 中点必落在墙体内(分隔线本就沿墙走) → 取夹住中点的那对交点，缺哪侧就用分隔线自身的端点(0/1)。
  const from = Math.max(0, ...ratios.filter((r) => r < 0.5 - thresholdValue));
  const to = Math.min(1, ...ratios.filter((r) => r > 0.5 + thresholdValue));
  if (!(to > from)) return seg;
  return trim(seg, from, to);
}

/**
 * 计算隔墙玻璃/半玻璃/虚墙的分隔辅助线，统一供已落地隔墙与绘制预览复用。
 * 弧墙沿弧线生成（全玻璃为两条偏移弧，半玻璃/虚墙为中线弧）；直墙生成偏移直线或中线。
 * 传入 shape 时把分隔线裁剪到墙体轮廓环（与已落地隔墙一致），不传则保持完整线段（预览行为）。
 */
export function calAuxiliarySegsCore(params: IAuxiliarySegParams): IShapeSeg2d[] {
  const { start, end, thickness, type, center, clockwise, shape } = params;

  if (center) {
    const arc = new ArcSeg2d({
      start,
      end,
      center,
      clockwise: clockwise ?? false
    });

    if (type === ESeparateWallType.GlassWall) {
      const radiusOffset = (thickness / 6) * (arc.clockwise ? 1 : -1);
      return [clipToShape(arc.getOffsetArc(radiusOffset), shape), clipToShape(arc.getOffsetArc(-radiusOffset), shape)];
    }

    if (type === ESeparateWallType.HalfGlassWall || type === ESeparateWallType.VirtualWall) {
      return [clipToShape(arc, shape)];
    }

    return [];
  }

  const separateSeg = new LineSeg2d(start, end);

  if (type === ESeparateWallType.GlassWall) {
    const { leftCurve, rightCurve } = Curve2dUtils.offsetCurves(separateSeg, thickness / 6);
    return [clipToShape(leftCurve, shape), clipToShape(rightCurve, shape)];
  }

  if (type === ESeparateWallType.HalfGlassWall || type === ESeparateWallType.VirtualWall) {
    return [clipToShape(separateSeg, shape)];
  }

  return [];
}

/**
 * 将一条形状段（直线段或弧段）离散为纯点序列；弧段按默认细分采样。
 */
export function shapeSegToPoints(seg: IShapeSeg2d): IPoint2d[] {
  return seg instanceof ArcSeg2d ? seg.getPoints() : [seg.start, seg.end];
}
